// features/automationScripts/utils/dbAssertionKeys.js
//
// What a database step can be asserted on, and which of those keys a row
// selector means anything for.
//
// An API step asserts on a response: one object, one set of paths, and the
// standard extras (status code, response time) are the same on every request.
// A database step asserts on a *result set*, so there are two kinds of key:
//
//   - result-level  — a property of the whole set (rowCount, rowsAffected,
//                     an aggregate the query itself computed, uniqueness of a
//                     column). There is no row to point at, so `row` is
//                     meaningless and the editor hides it.
//   - column values — a column, in the row named by `row`. With no `row` this
//                     is the first row, which is how database assertions have
//                     always behaved, so nothing existing changes.
//
// Aggregates are derived from the query rather than typed by hand: the engine
// names SUM(t.amount) as sum_amount and SUM(a.x) - SUM(b.x) AS diff as diff,
// so reading the SELECT list is the only way to offer them.

/** Result-level keys every database step has, whatever the query. */
export const DB_RESULT_KEYS = [
  { path: 'rowCount', hint: 'rows returned' },
  { path: 'rowsAffected', hint: 'rows changed by a write' },
];

/** Older spellings of rowCount the engine still accepts, so a saved step keeps working. */
const LEGACY_ROW_COUNT_KEYS = new Set(['row_count', '_metadata_row_count']);

/** The uniqueness prefix — a property of a column across the whole set. */
export const DB_UNIQUE_PREFIX = '_unique.';

/** SQL functions the engine gives a plain key to. */
const AGGREGATE_FUNCTIONS = ['count', 'sum', 'avg', 'max', 'min'];

const AGGREGATE_KEY_RE = new RegExp(`^(${AGGREGATE_FUNCTIONS.join('|')})(_[a-z0-9_]+)?$`, 'i');

/**
 * Which row an assertion is about. The empty value is deliberate: an assertion
 * with no `row` is the first row, and writing that out explicitly would change
 * the JSON of every step that already exists.
 */
export const DB_ROW_SELECTORS = [
  { value: '', label: 'First row (default)' },
  { value: 'first', label: 'First row' },
  { value: 'last', label: 'Last row' },
  { value: 'any', label: 'Any row' },
  { value: 'all', label: 'All rows' },
  { value: 'none', label: 'No row' },
  { value: 'index', label: 'Row number…' },
];

/** The sentinel the selector uses while a positional row is being typed. */
export const DB_ROW_INDEX_OPTION = 'index';

const NAMED_ROW_SELECTORS = new Set(['first', 'last', 'any', 'all', 'none']);

const isPositional = (row) => row !== '' && row != null && /^\d+$/.test(String(row).trim());

/** The key without the optional response. prefix the engine allows everywhere. */
export const stripResponsePrefix = (key) => String(key || '').replace(/^response\./, '');

/**
 * True when the key names a property of the whole result set rather than a
 * value in one row — rowCount, rowsAffected, an aggregate, or uniqueness.
 */
export const isResultLevelKey = (key) => {
  const bare = stripResponsePrefix(key).trim();
  if (!bare) return false;
  if (bare.startsWith(DB_UNIQUE_PREFIX)) return true;
  if (LEGACY_ROW_COUNT_KEYS.has(bare.toLowerCase())) return true;
  if (bare === 'rowCount' || bare === 'rowsAffected') return true;
  return AGGREGATE_KEY_RE.test(bare);
};

/**
 * True when a row selector is worth showing for this key. Result-level keys
 * have no row to point at; anything else is a column value.
 */
export const supportsRowSelector = (key) => {
  const bare = stripResponsePrefix(key).trim();
  if (!bare) return false;
  return !isResultLevelKey(bare);
};

/**
 * The row as it goes on the wire: absent when unset, a number when positional,
 * the named selector otherwise.
 */
export const serialiseRow = (row) => {
  if (row === '' || row == null) return undefined;
  const text = String(row).trim();
  if (text === '' || text === DB_ROW_INDEX_OPTION) return undefined;
  if (isPositional(text)) return Number(text);
  return NAMED_ROW_SELECTORS.has(text.toLowerCase()) ? text.toLowerCase() : undefined;
};

/** The stored row read back off a saved step, as the editor holds it. */
export const parseRow = (row) => {
  if (row === '' || row == null) return '';
  if (typeof row === 'number') return Number.isInteger(row) && row >= 0 ? String(row) : '';
  const text = String(row).trim().toLowerCase();
  if (isPositional(text)) return text;
  return NAMED_ROW_SELECTORS.has(text) ? text : '';
};

/** Which option the selector shows for a stored value. */
export const rowSelectorOption = (row) => (
  isPositional(row) ? DB_ROW_INDEX_OPTION : (parseRow(row) || '')
);

/**
 * How a row selector reads in a sentence — "in any row", "in row 2" — so a
 * results view can say which rows a check covered.
 */
export const describeRow = (row) => {
  if (row === '' || row == null) return '';
  const text = String(row).trim().toLowerCase();
  if (isPositional(text)) return `in row ${Number(text)}`;
  switch (text) {
    case 'first': return 'in the first row';
    case 'last': return 'in the last row';
    case 'any': return 'in any row';
    case 'all': return 'in all rows';
    case 'none': return 'in no row';
    default: return '';
  }
};

/* ── Aggregates from the query ─────────────────────────────────────── */

/** Split a comma-separated SQL list, ignoring commas inside brackets or quotes. */
const splitTopLevel = (text) => {
  const parts = [];
  let buf = '';
  let depth = 0;
  let quote = '';
  for (const ch of text) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = '';
      continue;
    }
    if (ch === "'" || ch === '"') { quote = ch; buf += ch; continue; }
    if (ch === '(') { depth += 1; buf += ch; continue; }
    if (ch === ')') { depth -= 1; buf += ch; continue; }
    if (ch === ',' && depth === 0) { parts.push(buf); buf = ''; continue; }
    buf += ch;
  }
  if (buf.trim()) parts.push(buf);
  return parts.map((p) => p.trim()).filter(Boolean);
};

/** The SELECT list of a query, or null when it is not a readable SELECT. */
const selectList = (sql) => {
  const flat = String(sql || '').replace(/\s+/g, ' ').trim().replace(/;$/, '');
  if (!/^select\s/i.test(flat)) return null;
  // The first top-level FROM ends the list; a FROM inside a subquery does not.
  let depth = 0;
  let quote = '';
  for (let i = 0; i < flat.length; i += 1) {
    const ch = flat[i];
    if (quote) { if (ch === quote) quote = ''; continue; }
    if (ch === "'" || ch === '"') { quote = ch; continue; }
    if (ch === '(') { depth += 1; continue; }
    if (ch === ')') { depth -= 1; continue; }
    if (depth === 0 && /^\sfrom\s/i.test(flat.slice(i, i + 6))) {
      return flat.slice(6, i).trim();
    }
  }
  return flat.slice(6).trim();
};

const ALIAS_RE = /\s+as\s+"?([a-z_][a-z0-9_]*)"?$/i;
const TRAILING_ALIAS_RE = /\)\s+"?([a-z_][a-z0-9_]*)"?$/i;
const FUNCTION_CALL_RE = new RegExp(`\\b(${AGGREGATE_FUNCTIONS.join('|')})\\s*\\(([^()]*)\\)`, 'i');

/** t.amount → amount, DISTINCT id → id, "Amount" → Amount. */
const bareColumn = (arg) => String(arg || '')
  .trim()
  .replace(/^distinct\s+/i, '')
  .replace(/^.*\./, '')
  .replace(/["'`]/g, '')
  .trim();

/**
 * The aggregate keys a query produces, in the engine's naming:
 *
 *   SELECT COUNT(*)                       → count
 *   SELECT SUM(t.amount)                  → sum_amount
 *   SELECT MAX(created_at), MIN(id)       → max_created_at, min_id
 *   SELECT SUM(a.x) - SUM(b.x) AS diff    → diff
 *
 * An explicit alias always wins — that is the name the value comes back under.
 */
export const deriveAggregateKeys = (sql) => {
  const list = selectList(sql);
  if (!list || list === '*') return [];

  const keys = [];
  const seen = new Set();
  const add = (key) => {
    if (!key || seen.has(key)) return;
    seen.add(key);
    keys.push(key);
  };

  splitTopLevel(list).forEach((item) => {
    if (!item.includes('(')) return; // a plain column, not something the query computed
    const aliased = item.match(ALIAS_RE) || item.match(TRAILING_ALIAS_RE);
    if (aliased) { add(aliased[1]); return; }
    const call = item.match(FUNCTION_CALL_RE);
    if (!call) return;
    const fn = call[1].toLowerCase();
    const arg = call[2].trim();
    if (arg === '*' || arg === '') { add(fn); return; }
    const column = bareColumn(arg);
    add(column ? `${fn}_${column}` : fn);
  });

  return keys;
};

/* ── What the editor offers ────────────────────────────────────────── */

/**
 * The assertion keys a database step can pick from, grouped for the path
 * dropdown. Column values stay sourced from the step's extractions — the
 * dropdown has always worked that way, and the pre-run check still verifies it.
 */
export const buildDbAssertionGroups = ({ extractedColumns = [], query = '' } = {}) => {
  const columns = extractedColumns.filter(Boolean);
  const aggregates = deriveAggregateKeys(query);

  const groups = [];

  if (columns.length > 0) {
    groups.push({
      key: 'dbColumns',
      items: columns.map((col) => ({ path: `response.${col}`, label: col })),
    });
  }

  groups.push({
    key: 'dbResult',
    items: DB_RESULT_KEYS.map(({ path, hint }) => ({ path, label: path, hint })),
  });

  if (aggregates.length > 0) {
    groups.push({
      key: 'dbAggregates',
      items: aggregates.map((key) => ({ path: key, label: key, hint: 'from the query' })),
    });
  }

  if (columns.length > 0) {
    groups.push({
      key: 'dbUnique',
      items: columns.map((col) => ({
        path: `${DB_UNIQUE_PREFIX}${col}`,
        label: `${DB_UNIQUE_PREFIX}${col}`,
        hint: 'no duplicates',
      })),
    });
  }

  return groups;
};

/**
 * True when a saved assertion key still resolves against the step as it now
 * stands. Result-level keys are always fine; a column value has to still be
 * extracted, which is what the pre-run check reports on.
 */
export const isKnownDbAssertionKey = (key, { extractedColumns = [] } = {}) => {
  const bare = stripResponsePrefix(key).trim();
  if (!bare) return true; // an empty key is reported as unconfigured elsewhere
  if (bare.startsWith(DB_UNIQUE_PREFIX)) {
    return extractedColumns.includes(bare.slice(DB_UNIQUE_PREFIX.length));
  }
  if (isResultLevelKey(bare)) return true;
  return extractedColumns.includes(bare);
};
